// WorkspaceSwitcherV2 — bộ đổi workspace trên thanh trên cùng v2. DÙNG CHUNG hai hệ SaaS
// (CNV Work · CNV Connect). Core KHÔNG mount: bản cài riêng cho MỘT khách, không có
// workspace nào để đổi.
//
// Vì sao ở kernel chứ không ở host: hai hệ SaaS là hai sản phẩm khác nhau nhưng CÙNG một
// nền tảng tenant (`/auth/me/tenants`, `/auth/me/switch-tenant`, cùng ADR-015). Bản đầu
// 21/08 em đặt ở host và bắt đầu chép sang hệ thứ hai — sếp Phú chặn đúng lúc: hai bản chép
// của 230 dòng là đúng thứ "drift FE/BE" mà CLAUDE.md cấm. Nay MỘT bản ở đây, host chỉ đưa
// vào những mảnh THẬT SỰ khác nhau.
//
// Mảnh khác nhau giữa hai hệ — và CHỈ có bấy nhiêu:
//   • kho dữ liệu phiên (authStore) của mỗi host   → listTenants / switchTenant / applySession
//   • MÔ HÌNH GÓI: Work có thang bậc số (`meta.plans.tier_level` 0/1/2) + bảng tên sản phẩm
//     (planCopy); Connect còn ở đời trước, bậc là chuỗi 'none'|'free'|'pro' và chưa có bảng
//     tên                                          → currentPlan / planLabelOf
//   • điều hướng                                    → onSeeAll
//
// ── Bốn điểm dễ làm sai, đừng gỡ ──
// 1. Danh sách CHỈ tải khi mở popup (`enabled: open`). Thanh trên cùng có mặt ở MỌI trang;
//    tải sẵn là thêm một request mỗi lần mở app chỉ để phục vụ một cú bấm hiếm.
// 2. Viên nhãn gói trên nút KHÔNG tự gọi API — host truyền `currentPlan` lấy từ chính query
//    `['seat-status']` mà cụm "gói đang dùng" (PlanBadgeV2) đã tải ⇒ 0 request phát sinh.
// 3. Đổi xong thì TẢI LẠI TRANG, không điều hướng mềm. Cache react-query đang giữ dữ liệu
//    của workspace cũ; `navigate()` sẽ hiện dữ liệu workspace A dưới cái tên workspace B —
//    sai loại nghiêm trọng nhất trong hệ multi-tenant.
// 4. `switchTenant` trả refreshToken MỚI (claim mang active_tenant_id). Host PHẢI lưu lại
//    trong `applySession`, nuốt mất thì ~15 phút sau lần refresh kế tiếp kéo người dùng về
//    workspace cũ.
//
// Nút hiện KỂ CẢ khi tài khoản chỉ có MỘT workspace (sếp Phú chốt 21/08): tên workspace là
// ngữ cảnh của mọi dữ liệu bên dưới; giấu đi thì người dùng một workspace không bao giờ đọc
// được mình đang ở đâu, và lúc được mời thêm workspace thứ hai thì nút "mọc" ra bất ngờ.
//
// CSS: src/styles/ui-v2-workspace.css — tệp RIÊNG, chỉ hai host SaaS import. Không nhét vào
// ui-v2.css vì Core cũng nạp tệp đó mà không bao giờ dựng cụm này.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, ChevronDown, LayoutGrid, Search } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

function WorkspaceRow({ row, isActive, switching, planLabel, onPick }) {
  const { t } = useLingui();
  const logo = row.branding?.logo_url;
  const name = row.display_name || row.slug;

  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      className={`v2-ws__item${isActive ? ' is-active' : ''}`}
      onClick={() => onPick(row)}
      disabled={!!switching || row.status === 'terminated'}
    >
      <span className="v2-ws__avatar">
        {logo ? <img src={logo} alt="" /> : (name?.[0] || '?').toUpperCase()}
      </span>
      <span className="v2-ws__body">
        <span className="v2-ws__name">{name}</span>
        {/* Dịch NGUYÊN CÂU (t`Mã ${x}`) chứ không ghép <Trans>Mã</Trans> + biến: tiếng
            Anh/Pháp đảo trật tự, ghép mảnh là dịch được từng chữ mà câu vẫn sai. */}
        <span className="v2-ws__meta">
          {row.code ? t`Mã ${row.code}` : row.slug}
          <i aria-hidden="true">·</i>
          {planLabel || t`Chưa kích hoạt`}
          {row.status && row.status !== 'active'
            ? <><i aria-hidden="true">·</i><em>{row.status}</em></>
            : null}
        </span>
      </span>
      {switching === row.tenant_id ? (
        <span className="v2-ws__going"><Trans>Đang vào...</Trans></span>
      ) : isActive ? (
        <Check size={16} className="v2-ws__check" aria-hidden="true" />
      ) : null}
    </button>
  );
}

export default function WorkspaceSwitcherV2({
  activeTenant,                  // { tenant_id, display_name, slug } — phiên đang mở
  currentPlan = null,            // { label, tier } — viên nhãn trên nút; null = không dựng
  listTenants,                   // async () => rows[]  (GET /auth/me/tenants)
  switchTenant,                  // async (tenant_id) => res
  applySession,                  // (res) => void — host ghi vào store của mình
  planLabelOf = (row) => row?.plan_label || null,   // (row) => tên gói ĐÃ dịch của dòng đó
  onSeeAll,                      // () => void — sang màn chọn workspace đầy đủ
}) {
  const { t } = useLingui();

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [switching, setSwitching] = useState(null);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-tenants'],
    queryFn: () => listTenants(),
    enabled: open && typeof listTenants === 'function',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Đóng khi bấm ra ngoài / bấm Esc — cùng cách UserMenu làm: mousedown chứ không click,
  // để popup đóng TRƯỚC khi phần tử bên dưới nhận cú bấm.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Mở ra là gõ được ngay — người có 10 workspace thì ô tìm nhanh hơn con lăn chuột.
  useEffect(() => { if (open) searchRef.current?.focus(); }, [open]);

  const list = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const q = term.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.display_name, r.code, r.slug, r.org_name, r.plan_label]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [data, term]);

  if (!activeTenant) return null;

  const currentName = activeTenant.display_name || activeTenant.slug || t`Workspace`;

  async function pick(row) {
    if (row.tenant_id === activeTenant.tenant_id) { setOpen(false); return; }
    if (row.status === 'terminated') {
      toast.error(t`Workspace đã chấm dứt — không vào được`);
      return;
    }
    setSwitching(row.tenant_id);
    try {
      const res = await switchTenant(row.tenant_id);
      applySession?.(res);
      // Xem ghi chú (3) đầu tệp: PHẢI tải lại, không điều hướng mềm.
      window.location.assign('/');
    } catch (e) {
      toast.error(apiErrorMessage(e, t`Đổi workspace thất bại`));
      setSwitching(null);
    }
  }

  return (
    <div className="v2-ws" ref={wrapRef}>
      <button
        type="button"
        className="v2-ws__btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t`Đổi workspace`}
      >
        <span className="v2-ws__btn-name">{currentName}</span>
        {/* Viên nhãn gói, đúng mẫu CNV CDP. Workspace chưa có gói thì KHÔNG dựng viên rỗng:
            cụm "gói đang dùng" bên phải thanh đã nói "Chưa kích hoạt", thêm một viên nữa là
            hai lần cùng một tin trên một thanh 38px. `tier` để CSS tô màu — Work truyền số
            (0/1/2), Connect truyền chuỗi ('free'/'pro'), CSS khai cả hai. */}
        {currentPlan?.label ? (
          <span className="v2-ws__btn-plan" data-tier={currentPlan.tier ?? ''}>
            {currentPlan.label}
          </span>
        ) : null}
        <ChevronDown size={13} className="v2-ws__chev" aria-hidden="true" />
      </button>

      {open ? (
        <div className="v2-ws__pop" role="listbox">
          <div className="v2-ws__head"><Trans>DANH SÁCH WORKSPACE</Trans></div>

          <div className="v2-ws__search">
            <Search size={14} aria-hidden="true" />
            <input
              ref={searchRef}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t`Tìm kiếm...`}
              aria-label={t`Tìm workspace`}
            />
          </div>

          <div className="v2-ws__list">
            {isLoading ? (
              <div className="v2-ws__empty"><Trans>Đang tải workspace...</Trans></div>
            ) : isError ? (
              <div className="v2-ws__empty"><Trans>Không tải được danh sách workspace</Trans></div>
            ) : !list.length ? (
              <div className="v2-ws__empty"><Trans>Không có workspace nào khớp</Trans></div>
            ) : list.map((row) => (
              <WorkspaceRow
                key={row.tenant_id}
                row={row}
                isActive={row.tenant_id === activeTenant.tenant_id}
                switching={switching}
                planLabel={planLabelOf(row)}
                onPick={pick}
              />
            ))}
          </div>

          {/* Chưa hệ nào cho tạo workspace ngay trong app (workspace mới đi qua đăng ký /
              lời mời), nên chỗ nút "tạo" của mẫu là lối sang màn chọn workspace đầy đủ —
              nơi xem được vai trò, tổ chức, module của từng workspace. */}
          {onSeeAll ? (
            <button type="button" className="v2-ws__foot" onClick={() => { setOpen(false); onSeeAll(); }}>
              <LayoutGrid size={14} aria-hidden="true" />
              <Trans>Xem tất cả workspace</Trans>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
